import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import MainLayout from './components/layout/MainLayout';
import ScrollToTop from './components/ui/ScrollToTop';
import Home from './pages/Home';
import Login from './pages/Login';
import OAuthCallback from './pages/OAuthCallback';
import DiscountCatalog from './pages/DiscountCatalog';
import DiscountDetails from './pages/DiscountDetails';
import Profile from './pages/Profile';
import UsageHistory from './pages/UsageHistory';
import NotFound from './pages/NotFound';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import BrowserCompatibility from './components/browser/BrowserCompatibility';
import analyticsTracker from './utils/analyticsTracker';

// Lazy load admin pages
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminDiscounts = lazy(() => import('./pages/admin/Discounts'));
const AdminPartners = lazy(() => import('./pages/admin/Partners'));
const AdminCategories = lazy(() => import('./pages/admin/Categories'));
const AdminAnalytics = lazy(() => import('./pages/admin/Analytics'));

function App() {
  const { isAuthenticated, loading, checkAuth } = useAuth();
  const location = useLocation();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Ініціалізація аналітики
  useEffect(() => {
    analyticsTracker.init();
  }, []);

  // Відстеження змін сторінок
  useEffect(() => {
    if (!loading) {
      analyticsTracker.trackPageView(location.pathname, document.title);
    }
  }, [location, loading]);

  if (loading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <>
      {/* Компонент для перевірки сумісності браузера */}
      <BrowserCompatibility />

      {/* Компонент для скролу до верху при зміні маршруту */}
      <ScrollToTop />
      
      <Routes>
        {/* Публічні маршрути */}
        <Route path="/login" element={<Login />} />
        <Route path="/oauth-callback" element={<OAuthCallback />} />
        
        {/* Захищені маршрути */}
        <Route element={<ProtectedRoute isAuthenticated={isAuthenticated} />}>
          {/* Основні користувацькі сторінки з головним лейаутом */}
          <Route element={<MainLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/discounts" element={<DiscountCatalog />} />
            <Route path="/discounts/:id" element={<DiscountDetails />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/usage-history" element={<UsageHistory />} />
          </Route>

          {/* Адмінські маршрути БЕЗ загального хедера/футера */}
          <Route element={<AdminRoute />}>
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/admin/dashboard" element={<Suspense fallback={<div className="flex h-screen items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500" /></div>}><AdminDashboard /></Suspense>} />
            <Route path="/admin/users" element={<Suspense fallback={null}><AdminUsers /></Suspense>} />
            <Route path="/admin/discounts" element={<Suspense fallback={null}><AdminDiscounts /></Suspense>} />
            <Route path="/admin/partners" element={<Suspense fallback={null}><AdminPartners /></Suspense>} />
            <Route path="/admin/categories" element={<Suspense fallback={null}><AdminCategories /></Suspense>} />
            <Route path="/admin/analytics" element={<Suspense fallback={null}><AdminAnalytics /></Suspense>} />
          </Route>
        </Route>
        
        {/* 404 маршрут */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

export default App;
